/**
 * TrendChart
 *
 * Dependency-free time-series chart. Renders vertical bars (default) or a line
 * — optionally with a lighter second line for a previous-period comparison —
 * from a data series using plain CSS/SVG, no charting library required.
 * Designed to be reusable across analytics tools (Redirects, Banners,
 * Promoters, Engagement).
 *
 * Features:
 *  - Responsive (bars/lines flex to fill width)
 *  - Hover tooltip with label + value (+ comparison value, if present)
 *  - Value labels shown inline for short ranges (<= maxInlineLabels points, bar mode only)
 *  - Sparse x-axis labels for long ranges (first / mid / last + every Nth)
 *  - Max-value gridline label
 *  - Loading + empty states
 *
 * Usage:
 *   <TrendChart
 *     data={[{ label: '2026-06-01', value: 12 }, ...]}
 *     color="#4299E1"
 *     height={200}
 *   />
 *
 *   <TrendChart
 *     data={currentPeriod}
 *     comparisonData={previousPeriod}
 *     mode="line"
 *     color="#4299E1"
 *   />
 */

import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import "./TrendChart.scss";

// Line-mode SVG points — a fixed 0-100 X range (stretched to fill via
// preserveAspectRatio="none") and real pixel Y range, so gridlines (drawn with
// plain % CSS positioning) line up with the plotted values exactly.
function seriesToPoints(series, max, height, min = 0) {
  if (!series || series.length === 0) return "";
  const stepX = series.length > 1 ? 100 / (series.length - 1) : 0;
  return series
    .map((d, i) => {
      const x = series.length > 1 ? i * stepX : 50;
      const y = height - (((d.value || 0) - min) / (max - min || 1)) * height;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

const TrendChart = ({
  data = [],
  comparisonData = null,
  comparisonLabel = "Previous period",
  mode = "bar",
  height = 200,
  color = "#4299E1",
  loading = false,
  emptyText = "No data for this range",
  valueFormatter = (v) => v.toLocaleString(),
  /**
   * Formatter for the AXIS bounds only. Defaults to valueFormatter, but the
   * two want different precision: a tooltip reading 99.577% is useful, while
   * an axis reading "100.000%" is three zeros of noise.
   */
  axisFormatter = null,
  labelFormatter = (l) => l,
  maxInlineLabels = 14,
  // Opt out of the inline bar values. A series whose bars reach the top (an
  // uptime chart sitting at 100%) collides its labels with the max gridline,
  // and the hover tooltip already carries the number.
  showValues = true,
  /**
   * Baseline of the y-axis. Defaults to 0, which is right for counts — a bar
   * chart of visits must start at zero or it lies about proportion.
   *
   * For a series that lives near a ceiling (uptime at 99.x%) zero makes every
   * bar full height and a real dip invisible. Passing a min zooms the axis so
   * the variation is legible, and the base gridline is labelled so the reader
   * can see the axis does not start at zero.
   */
  min = 0,
  /**
   * A horizontal threshold drawn across the plot — {value, label}. Built for
   * a free-tier allowance: a bar chart of usage answers "how much", but only
   * a line at the allowance answers the question actually being asked, which
   * is "how far over are we". Included in the axis max so a threshold ABOVE
   * every bar still appears (usage under its allowance is exactly the case
   * worth being able to see).
   */
  referenceLine = null,
  /**
   * Where the axis values sit.
   *
   * "overlay" (default) prints them inside the plot at the right edge, which
   * is fine when bars are short but collides with a full-height bar — the
   * value vanishes exactly when the chart is at its ceiling. "gutter" reserves
   * a strip to the right so both bounds stay readable whatever the bars do.
   * Opt-in, so existing charts keep the layout they were designed with.
   */
  axisLabels = "overlay",
  /** Called with (datum, index) when a bar is clicked. */
  onPointClick = null,
  className = "",
}) => {
  const [hover, setHover] = useState(null); // index of hovered point

  const isLine = mode === "line";
  const hasComparison =
    isLine &&
    Array.isArray(comparisonData) &&
    comparisonData.length > 0 &&
    comparisonData.length === data.length;

  const max = useMemo(() => {
    const values = data.map((d) => d.value || 0);
    if (hasComparison) values.push(...comparisonData.map((d) => d.value || 0));
    if (referenceLine && Number.isFinite(referenceLine.value))
      values.push(referenceLine.value);
    return Math.max(1, ...values);
  }, [data, comparisonData, hasComparison, referenceLine]);

  const total = useMemo(
    () => data.reduce((sum, d) => sum + (d.value || 0), 0),
    [data],
  );

  // Decide which x-axis labels to show so they don't overlap on long ranges.
  const labelStep = useMemo(() => {
    if (data.length <= 10) return 1;
    // 8 labels crowd at 30+ points and collide outright at 90 — the last two
    // dates were overprinting each other. Thin further as the range grows.
    const target = data.length > 45 ? 5 : data.length > 20 ? 6 : 8;
    return Math.ceil(data.length / target);
  }, [data.length]);

  const showInlineValues =
    showValues && !isLine && data.length > 0 && data.length <= maxInlineLabels;

  const linePoints = useMemo(
    () => (isLine ? seriesToPoints(data, max, height, min) : ""),
    [isLine, data, max, height, min],
  );
  const comparisonPoints = useMemo(
    () =>
      hasComparison ? seriesToPoints(comparisonData, max, height, min) : "",
    [hasComparison, comparisonData, max, height, min],
  );

  if (loading) {
    return (
      <div
        className={`trend-chart trend-chart--loading ${className}`}
        style={{ height }}
      >
        <div className="trend-chart__spinner" />
        <span className="trend-chart__loading-text">Loading chart…</span>
      </div>
    );
  }

  if (!data.length || total === 0) {
    return (
      <div
        className={`trend-chart trend-chart--empty ${className}`}
        style={{ height }}
      >
        <span className="trend-chart__empty-text">{emptyText}</span>
      </div>
    );
  }

  return (
    <div className={`trend-chart ${className}`}>
      {hasComparison && (
        <div className="trend-chart__legend">
          <span className="trend-chart__legend-item" style={{ color }}>
            <span
              className="trend-chart__legend-swatch"
              style={{ background: color }}
            />
            This period
          </span>
          <span
            className="trend-chart__legend-item trend-chart__legend-item--muted"
            style={{ color }}
          >
            <span className="trend-chart__legend-swatch trend-chart__legend-swatch--dashed" />
            {comparisonLabel}
          </span>
        </div>
      )}
      <div
        className={`trend-chart__plot ${
          axisLabels === "gutter" ? "trend-chart__plot--gutter" : ""
        }`}
        style={{ height }}
      >
        {/* Max gridline */}
        <div className="trend-chart__gridline trend-chart__gridline--top">
          <span className="trend-chart__gridline-label">
            {(axisFormatter || valueFormatter)(max)}
          </span>
        </div>
        <div className="trend-chart__gridline trend-chart__gridline--mid" />
        <div className="trend-chart__gridline trend-chart__gridline--base">
          {min > 0 && (
            <span className="trend-chart__gridline-label">
              {(axisFormatter || valueFormatter)(min)}
            </span>
          )}
        </div>

        {referenceLine && Number.isFinite(referenceLine.value) && (
          <div
            className="trend-chart__refline"
            style={{
              bottom: `${Math.min(100, Math.max(0, ((referenceLine.value - min) / (max - min || 1)) * 100))}%`,
            }}
          >
            {referenceLine.label && (
              <span className="trend-chart__refline-label">
                {referenceLine.label}
              </span>
            )}
          </div>
        )}

        {isLine && (
          <svg
            className="trend-chart__line-svg"
            viewBox={`0 0 100 ${height}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {hasComparison && (
              <polyline
                points={comparisonPoints}
                className="trend-chart__line-path trend-chart__line-path--comparison"
                style={{ stroke: color }}
              />
            )}
            <polyline
              points={linePoints}
              className="trend-chart__line-path"
              style={{ stroke: color }}
            />
          </svg>
        )}

        <div className="trend-chart__bars">
          {data.map((d, i) => {
            const value = d.value || 0;
            const pctHeight = Math.round(
              ((value - min) / (max - min || 1)) * 100,
            );
            const comparePoint = hasComparison ? comparisonData[i] : null;
            const isHover = hover === i;
            return (
              <div
                key={d.label ?? i}
                className={`trend-chart__col ${
                  onPointClick ? "trend-chart__col--clickable" : ""
                }`}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                onClick={onPointClick ? () => onPointClick(d, i) : undefined}
                role={onPointClick ? "button" : undefined}
                tabIndex={onPointClick ? 0 : undefined}
                onKeyDown={
                  onPointClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onPointClick(d, i);
                        }
                      }
                    : undefined
                }
              >
                {isHover && (
                  <div className="trend-chart__tooltip">
                    <span className="trend-chart__tooltip-value">
                      {valueFormatter(value)}
                    </span>
                    <span className="trend-chart__tooltip-label">
                      {labelFormatter(d.label)}
                      {d.partial ? " · so far" : ""}
                    </span>
                    {comparePoint && (
                      <span className="trend-chart__tooltip-compare">
                        {comparisonLabel}:{" "}
                        {valueFormatter(comparePoint.value || 0)} (
                        {labelFormatter(comparePoint.label)})
                      </span>
                    )}
                  </div>
                )}
                {showInlineValues && value > 0 && (
                  <span className="trend-chart__bar-value">
                    {valueFormatter(value)}
                  </span>
                )}
                {!isLine && (
                  <div
                    className="trend-chart__bar"
                    style={{
                      height: `${pctHeight}%`,
                      // `partial` (e.g. today, still in progress) → candy-stripe
                      // fill so an incomplete day doesn't read as a traffic drop.
                      // A point may carry its own colour (e.g. a threshold
                      // scale); otherwise the whole series shares one.
                      background: (() => {
                        const c = d.color || color;
                        return d.partial
                          ? `repeating-linear-gradient(45deg, ${c}, ${c} 5px, ${c}73 5px, ${c}73 10px)`
                          : `linear-gradient(180deg, ${c}, ${c}cc)`;
                      })(),
                      // `muted` bars (context outside a focus window) sit faded;
                      // hovering any bar dims the others as usual.
                      opacity: d.muted
                        ? isHover
                          ? 0.6
                          : 0.28
                        : hover === null || isHover
                          ? 1
                          : 0.55,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Hover dots — drawn in a plot-level overlay (matching the line SVG's
            box) so they sit exactly on the line vertices. Positioning them per
            column would place them at column centres, which don't line up with
            the edge-to-edge line points. */}
        {isLine &&
          hover !== null &&
          (() => {
            const xf = data.length > 1 ? hover / (data.length - 1) : 0.5;
            const cur = data[hover];
            const cmp = hasComparison ? comparisonData[hover] : null;
            return (
              <div className="trend-chart__hover-layer">
                {cmp && (
                  <div
                    className="trend-chart__line-hover-dot trend-chart__line-hover-dot--comparison"
                    style={{
                      left: `${xf * 100}%`,
                      bottom: `${((cmp.value || 0) / max) * 100}%`,
                      color,
                    }}
                  />
                )}
                <div
                  className="trend-chart__line-hover-dot"
                  style={{
                    left: `${xf * 100}%`,
                    bottom: `${((cur.value || 0) / max) * 100}%`,
                    color,
                  }}
                />
              </div>
            );
          })()}
      </div>

      {/* X-axis labels */}
      <div
        className={`trend-chart__axis ${
          axisLabels === "gutter" ? "trend-chart__axis--gutter" : ""
        }`}
      >
        {data.map((d, i) => {
          const show = i === 0 || i === data.length - 1 || i % labelStep === 0;
          return (
            <div key={d.label ?? i} className="trend-chart__axis-col">
              {show && (
                <span className="trend-chart__axis-label">
                  {labelFormatter(d.label)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

TrendChart.propTypes = {
  /** Array of { label, value } points in chronological order */
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.number,
    }),
  ),
  /** Optional previous-period series (line mode only) — same length as `data`,
   *  plotted at the same X positions (index-aligned, not calendar-aligned). */
  comparisonData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.number,
    }),
  ),
  /** Legend/tooltip label for the comparison series */
  comparisonLabel: PropTypes.string,
  /** Formatter for axis bounds; defaults to valueFormatter */
  axisFormatter: PropTypes.func,
  /** 'overlay' (default) or 'gutter' — where axis values are drawn */
  axisLabels: PropTypes.oneOf(["overlay", "gutter"]),
  /** (datum, index) => void when a bar is clicked */
  onPointClick: PropTypes.func,
  /** Y-axis baseline; default 0. Use for series near a ceiling. */
  min: PropTypes.number,
  /** Horizontal threshold line, e.g. a free-tier allowance */
  referenceLine: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
  }),
  /** Show the value above each bar (bar mode, short ranges) */
  showValues: PropTypes.bool,
  /** 'bar' (default) or 'line' */
  mode: PropTypes.oneOf(["bar", "line"]),
  /** Plot height in px (excludes axis labels) */
  height: PropTypes.number,
  /** Bar/line colour */
  color: PropTypes.string,
  loading: PropTypes.bool,
  emptyText: PropTypes.string,
  /** Format a value for tooltip / gridline / inline label */
  valueFormatter: PropTypes.func,
  /** Format a label for tooltip / axis */
  labelFormatter: PropTypes.func,
  /** Max points at which inline bar values are drawn (bar mode only) */
  maxInlineLabels: PropTypes.number,
  className: PropTypes.string,
};

export default TrendChart;
